/**
 * CitationDrawer Component (DC-1061)
 * ============================================================================
 * Unified drawer for both regulatory and literature citations.
 * Slides in from right, shows citation details with tabs for Details/Raw.
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import type { RegulatoryCitation, CitationType } from '../../types/evidence';
import type { LiteratureCitationRef } from '../../types/bridgeIntelligence';
import './citation-drawer.css';

// ============================================================================
// Types
// ============================================================================

export interface CitationDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** The citation to display */
  citation: RegulatoryCitation | LiteratureCitationRef | null;
  /** Citation type */
  citationType: CitationType | null;
}

type TabId = 'details' | 'raw';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Type guard for regulatory citation
 */
function isRegulatoryCitation(
  citation: RegulatoryCitation | LiteratureCitationRef | null,
  type: CitationType | null
): citation is RegulatoryCitation {
  return type === 'regulatory' && citation !== null;
}

/**
 * Type guard for literature citation
 */
function isLiteratureCitation(
  citation: RegulatoryCitation | LiteratureCitationRef | null,
  type: CitationType | null
): citation is LiteratureCitationRef {
  return type === 'literature' && citation !== null;
}

/**
 * Get PubMed URL from PMID
 */
function getPubMedUrl(pmid: string): string {
  return `https://pubmed.ncbi.nlm.nih.gov/${pmid}`;
}

/**
 * Get DOI URL
 */
function getDoiUrl(doi: string): string {
  return `https://doi.org/${doi}`;
}

/**
 * Get relevance color class
 */
function getRelevanceClass(score: number): string {
  if (score >= 80) return 'citation-drawer__relevance--high';
  if (score >= 60) return 'citation-drawer__relevance--moderate';
  return 'citation-drawer__relevance--low';
}

// ============================================================================
// Component
// ============================================================================

export const CitationDrawer: React.FC<CitationDrawerProps> = ({
  isOpen,
  onClose,
  citation,
  citationType,
}) => {
  const [activeTab, setActiveTab] = useState<TabId>('details');
  const [copySuccess, setCopySuccess] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  // ============================================================================
  // Effects
  // ============================================================================

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      drawerRef.current.focus();
    }
  }, [isOpen]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleCopyToClipboard = async () => {
    if (!citation) return;
    try {
      let text = '';
      if (isRegulatoryCitation(citation, citationType)) {
        text = `${citation.framework} - ${citation.document}, ${citation.section}\n\n"${citation.text}"\n\nSource: ${citation.url}`;
      } else if (isLiteratureCitation(citation, citationType)) {
        text = `${citation.title}\n\n${citation.authors}. ${citation.journal || ''}. ${citation.year || ''}\n\nPMID: ${citation.pmid}\n${citation.doi ? `DOI: ${citation.doi}` : ''}`;
      }
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const renderRegulatoryCitation = (reg: RegulatoryCitation) => (
    <>
      {/* Framework Badge */}
      <div className="citation-drawer__badge-row">
        <span className="citation-drawer__framework-badge">{reg.framework}</span>
      </div>

      {/* Document & Section */}
      <div className="citation-drawer__field">
        <span className="citation-drawer__label">Document</span>
        <span className="citation-drawer__value">{reg.document}</span>
      </div>
      <div className="citation-drawer__field">
        <span className="citation-drawer__label">Section</span>
        <span className="citation-drawer__value">{reg.section}</span>
      </div>

      {/* Quoted Text */}
      <div className="citation-drawer__field">
        <span className="citation-drawer__label">Citation Text</span>
        <blockquote className="citation-drawer__quote">
          "{reg.text}"
        </blockquote>
      </div>

      {/* Relevance */}
      <div className="citation-drawer__field">
        <span className="citation-drawer__label">Relevance</span>
        <span className="citation-drawer__value">{reg.relevance}</span>
      </div>

      {/* Version/Date */}
      {(reg.version || reg.publicationDate) && (
        <div className="citation-drawer__field-row">
          {reg.version && (
            <div className="citation-drawer__field">
              <span className="citation-drawer__label">Version</span>
              <span className="citation-drawer__value">{reg.version}</span>
            </div>
          )}
          {reg.publicationDate && (
            <div className="citation-drawer__field">
              <span className="citation-drawer__label">Publication Date</span>
              <span className="citation-drawer__value">{reg.publicationDate}</span>
            </div>
          )}
        </div>
      )}

      {/* Source Link */}
      <div className="citation-drawer__field">
        <span className="citation-drawer__label">Source</span>
        <a
          href={reg.url}
          target="_blank"
          rel="noopener noreferrer"
          className="citation-drawer__link"
        >
          {reg.url}
        </a>
      </div>
    </>
  );

  const renderLiteratureCitation = (lit: LiteratureCitationRef) => (
    <>
      {/* ID Badges */}
      <div className="citation-drawer__badge-row">
        <span className="citation-drawer__pmid-badge">PMID: {lit.pmid}</span>
        {lit.doi && (
          <a
            href={getDoiUrl(lit.doi)}
            target="_blank"
            rel="noopener noreferrer"
            className="citation-drawer__doi-badge"
          >
            DOI: {lit.doi}
          </a>
        )}
        {lit.full_text_available && (
          <span className="citation-drawer__fulltext-badge">Full Text</span>
        )}
      </div>

      {/* Title */}
      <div className="citation-drawer__field">
        <span className="citation-drawer__label">Title</span>
        <a
          href={getPubMedUrl(lit.pmid)}
          target="_blank"
          rel="noopener noreferrer"
          className="citation-drawer__title-link"
        >
          {lit.title}
        </a>
      </div>

      {/* Authors */}
      <div className="citation-drawer__field">
        <span className="citation-drawer__label">Authors</span>
        <span className="citation-drawer__value">{lit.authors}</span>
      </div>

      {/* Journal & Year */}
      <div className="citation-drawer__field-row">
        {lit.journal && (
          <div className="citation-drawer__field">
            <span className="citation-drawer__label">Journal</span>
            <span className="citation-drawer__value">{lit.journal}</span>
          </div>
        )}
        {lit.year && (
          <div className="citation-drawer__field">
            <span className="citation-drawer__label">Year</span>
            <span className="citation-drawer__value">{lit.year}</span>
          </div>
        )}
      </div>

      {/* Relevance Score */}
      <div className="citation-drawer__field">
        <span className="citation-drawer__label">Relevance Score</span>
        <div className="citation-drawer__relevance-container">
          <div className="citation-drawer__relevance-bar-bg">
            <div
              className={`citation-drawer__relevance-bar ${getRelevanceClass(lit.relevance_score)}`}
              style={{ width: `${lit.relevance_score}%` }}
            />
          </div>
          <span className={`citation-drawer__relevance-score ${getRelevanceClass(lit.relevance_score)}`}>
            {lit.relevance_score}%
          </span>
        </div>
      </div>

      {/* Relevance Explanation */}
      {lit.relevance_explanation && (
        <div className="citation-drawer__field">
          <span className="citation-drawer__label">Why Relevant</span>
          <span className="citation-drawer__value">{lit.relevance_explanation}</span>
        </div>
      )}

      {/* Key Quote */}
      {lit.key_quote && (
        <div className="citation-drawer__field">
          <span className="citation-drawer__label">Key Quote</span>
          <blockquote className="citation-drawer__quote">
            "{lit.key_quote}"
          </blockquote>
        </div>
      )}
    </>
  );

  // ============================================================================
  // Render
  // ============================================================================

  if (!isOpen || !citation || !citationType) return null;

  const jsonString = JSON.stringify(citation, null, 2);
  const drawerTitle = citationType === 'regulatory'
    ? 'Regulatory Citation'
    : 'Literature Citation';

  return (
    <>
      {/* Overlay */}
      <div
        className="citation-drawer__overlay"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="citation-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="citation-drawer-title"
        tabIndex={-1}
      >
        {/* Header */}
        <div className="citation-drawer__header">
          <div className="citation-drawer__header-content">
            <h2 id="citation-drawer-title" className="citation-drawer__title">
              {drawerTitle}
            </h2>
            <span className="citation-drawer__type-badge">
              {citationType === 'regulatory' ? 'Regulatory' : 'Scientific Literature'}
            </span>
          </div>
          <button
            type="button"
            className="citation-drawer__close-btn"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="citation-drawer__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            className={`citation-drawer__tab ${activeTab === 'details' ? 'citation-drawer__tab--active' : ''}`}
            aria-selected={activeTab === 'details'}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
          <button
            type="button"
            role="tab"
            className={`citation-drawer__tab ${activeTab === 'raw' ? 'citation-drawer__tab--active' : ''}`}
            aria-selected={activeTab === 'raw'}
            onClick={() => setActiveTab('raw')}
          >
            Raw Data
          </button>
        </div>

        {/* Content */}
        <div className="citation-drawer__content">
          {activeTab === 'details' ? (
            <div className="citation-drawer__details">
              {isRegulatoryCitation(citation, citationType)
                ? renderRegulatoryCitation(citation)
                : isLiteratureCitation(citation, citationType)
                  ? renderLiteratureCitation(citation)
                  : null
              }
            </div>
          ) : (
            <div className="citation-drawer__raw">
              <pre className="citation-drawer__json">{jsonString}</pre>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="citation-drawer__footer">
          <button
            type="button"
            className="citation-drawer__action-btn citation-drawer__action-btn--secondary"
            onClick={handleCopyToClipboard}
          >
            {copySuccess ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                Copy Citation
              </>
            )}
          </button>
          {isLiteratureCitation(citation, citationType) && (
            <a
              href={getPubMedUrl(citation.pmid)}
              target="_blank"
              rel="noopener noreferrer"
              className="citation-drawer__action-btn citation-drawer__action-btn--primary"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              View on PubMed
            </a>
          )}
          {isRegulatoryCitation(citation, citationType) && (
            <a
              href={citation.url}
              target="_blank"
              rel="noopener noreferrer"
              className="citation-drawer__action-btn citation-drawer__action-btn--primary"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              View Source
            </a>
          )}
        </div>
      </div>
    </>
  );
};

export default CitationDrawer;
